import {
  ICustomerForm,
  ICustomerPayload,
  useCreateCustomerMutation,
} from "@/redux/reducer/customer.api";
import {
  BootstrapSizes,
  Button,
  ButtonColors,
  Forms,
  Grid,
  TalentechFormFieldTypes,
} from "@talentech/components";
import { useRef } from "react";
import { toast } from "react-toastify";
import * as yup from "yup";
import { sanitizeCompanyName } from "@/shared/utils";

const Form = () => {
  const [createCustomer] = useCreateCustomerMutation();
  const submitForm = (data: { form: ICustomerForm }) => {
    // first letter of name & lastname   + company name + date timestamp
    const shortName =
      data.form.firstName.charAt(0) +
      data.form.lastName.charAt(0) +
      data.form.company

    const payload: ICustomerPayload = {
      customerName: sanitizeCompanyName(data.form.company),
      shortName: sanitizeCompanyName(shortName),
      promoCode: data.form.promoCode,
      tenantAdminUser: {
        firstName: data.form.firstName,
        lastName: data.form.lastName,
        email: data.form.email,
      },
      productList: [],
      defaultCulture: "pl-PL",
      defaultLanguage: "pl-PL",
    };

    toast
      .promise(createCustomer(payload).unwrap(), {
        error: "Something went wrong",
        success: "Customer created successfully",
        pending: "Creating customer...",
      })
      .then((response) => {
        setTimeout(() => {
          toast.info(
            "We're setting up your company, you will be redirected to the login page in a few seconds"
          );

          const createBrowserRedirectLink = (): string => {
            const tenantId = response.id
            const username = encodeURIComponent(response.tenantOwner.email);
            const returnUrl = encodeURIComponent("/Start");
            const domain = process.env.TID_API_URL;
            const url = new URL(
              `${domain}/Account/LocalLogin?tenantid=${tenantId}&username=${username}&returnUrl=${returnUrl}`
            );
            return url.toString();
          };

          window.location.href = createBrowserRedirectLink();
        }, 3000);
      });
  };

  const validationSchema = yup.object().shape({
    form: yup.object().shape({
      firstName: yup.string().required("First name is required"),
      lastName: yup.string().required("Last name is required"),
      email: yup.string().email("Invalid email").required("Email is required"),
      company: yup.string().required("Company is required"),
    }),
  });

  const formRef = useRef<HTMLFormElement>(null);

  return (
    <Grid container fluid className="bg-gray-100 py-4" id="rollout-form">
      <Grid
        item
        xs={12}
        className="d-flex justify-content-center align-items-center"
      >
        <div className="p-4 rounded-3 mt-4 bg-white">
          <h2 className="text-center mb-0 mx-4">
            Unleash HR platform for your company
          </h2>
        </div>
      </Grid>

      <Grid item xs={12} md={6} className="mt-4 py-4 offset-md-3">
        <Forms.Form
          reference={formRef}
          validationSchema={validationSchema}
          formFields={{
            id: "form",
            section: "Roll out your company with this brand new platform",
            fields: [
              {
                field: "firstName",
                label: "First name",
                type: TalentechFormFieldTypes.TEXT,
                size: {
                  sm: 6,
                },
              },
              {
                field: "lastName",
                label: "Last name",
                type: TalentechFormFieldTypes.TEXT,
                size: {
                  sm: 6,
                },
              },
              {
                field: "email",
                label: "Email",
                type: TalentechFormFieldTypes.TEXT,
                size: {
                  sm: 6,
                },
              },
              {
                field: "company",
                label: "Company",
                type: TalentechFormFieldTypes.TEXT,
                size: {
                  sm: 6,
                },
              },
              {
                field: "promoCode",
                label: "Promo code",
                type: TalentechFormFieldTypes.TEXT,
                size: {
                  sm: 12,
                },
              },
            ],
            description:
              "We will use this information to setup your company and provide you with the best possible service.",
          }}
          handleReset={() => {}}
          handleSubmit={submitForm}
        />
        <Button
          onClick={() => {
            formRef.current?.submitForm();
          }}
          color={ButtonColors.PRIMARY}
          size={BootstrapSizes.XLARGE}
          block={true}
          className="mt-4"
          label="Get started"
        />
      </Grid>
    </Grid>
  );
};

export default Form;
